import * as React from 'react';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import { Thead } from "./Thead";
import { Tbody } from "./Tbody";
import { useState } from 'react';
import { useMemo } from 'react';
import { CustomPagination } from './CustomPagination';
import { useEffect } from 'react';

/**
 * Permet de prendre en charge les propriétés imbriquées des objets à plusieurs niveaux
 * @param {*} obj 
 * @param {*} path 
 * @returns 
 */
function getNestedValue(obj, path) {
    if (!path || typeof path !== "string") return undefined;
    return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
}

/**
 * Fonction de comparaison pour le tri des données
 * @param {*} a 
 * @param {*} b 
 * @param {*} orderBy 
 * @returns 
 */
function descendingComparator(a, b, orderBy) {
    const aValue = getNestedValue(a, orderBy);
    const bValue = getNestedValue(b, orderBy);

    // Vérifie si aValue ou bValue est null ou undefined
    const aIsMissing = aValue === undefined || aValue === null;
    const bIsMissing = bValue === undefined || bValue === null;

    if (aIsMissing && !bIsMissing) {
        return 1; // Relègue 'a' à la fin
    }
    if (!aIsMissing && bIsMissing) {
        return -1; // Relègue 'b' à la fin
    }
    if (aIsMissing && bIsMissing) {
        return 0; // Considère que les deux valeurs manquantes sont égales
    }

    if (bValue < aValue) {
        return -1;
    }
    if (bValue > aValue) {
        return 1;
    }
    return 0;
}

/**
 * Récupère l'état actuel du comparateur
 * @param {*} order 
 * @param {*} orderBy 
 * @returns 
 */
function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Fonction de tri des données
 * @param {*} array 
 * @param {*} comparator 
 * @returns 
 */
function stableSort(array, comparator) {
    const stabilizedArray = array.map((el, index) => [el, index]);
    stabilizedArray.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedArray.map((el) => el[0]);
}

/**
 * Tableau générique personnalisable
 * reçois en entrée des données sous forme de tableau d'objets
 * le headers précises la liste des colonnes, le type de données et si des actions peuvent être réalisées
 * @param {*} param0 
 * @returns 
 */
export const CustomTable = ({ data, headers, initOrderByColumnIndex = 0, maxHeight='440px', paginate = false, rowsPerPage = 50, limit = false, stickyFirstColumn = false }) => {
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setCurrentPage(1);
    }, [data]);
    
    const [orderBy, setOrderBy] = React.useState(headers[initOrderByColumnIndex].key);
    const [order, setOrder] = React.useState('asc');

    //Tri sur les données reçues
    const sortedData = React.useMemo(() => {
        return stableSort(data, getComparator(order, orderBy));
    }, [data, order, orderBy]);

    //Gère l'appel au tri (clic sur le header d'une colonne)
    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setCurrentPage(1);
    };

    // Découpe les données triées selon la pagination
    const visibleData = useMemo(() => {
        if (!paginate) return sortedData;
        const start = (currentPage - 1) * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, currentPage, rowsPerPage, paginate]);

    return (
        <TableContainer sx={{ maxHeight: `${maxHeight}` }} className='custom-scrollbar'>
            <Table stickyHeader aria-label="sticky table" className='custom-table'>
                <Thead orderBy={orderBy} order={order} onRequestSort={handleRequestSort} headers={headers} stickyFirstColumn={stickyFirstColumn}/>
                <Tbody data={visibleData} headers={headers} stickyFirstColumn={stickyFirstColumn}/>
                {paginate && !limit && <CustomPagination
                    count={data.length}
                    page={currentPage}
                    rowsPerPage={rowsPerPage}
                    onPageChange={setCurrentPage}
                />}
            </Table>
        </TableContainer>
    );
}
